import type {
  Company,
  CreateNoticeInput,
  Notice,
  NoticeListQuery,
  UpdateNoticeInput,
  UserIdentity,
} from "dutyon-server/types";
import { formatNoticeDateKey } from "../utils/notice-date";
import { httpClient } from "./httpClient";

export { formatNoticeDateKey, noticeDateFromValue } from "../utils/notice-date";

export type NoticeWithCompany = Notice & {
  company: Company | null;
  updatedBy?: UserIdentity | null;
};

export type CreateNoticePayload = Omit<CreateNoticeInput, "startDate" | "endDate"> & {
  startDate: Date | string;
  endDate: Date | string;
};

export type UpdateNoticePayload = Omit<UpdateNoticeInput, "startDate" | "endDate"> & {
  startDate?: Date | string;
  endDate?: Date | string;
};

function normalizeNoticeDates<T extends UpdateNoticePayload>(data: T) {
  return {
    ...data,
    ...(data.startDate !== undefined ? { startDate: formatNoticeDateKey(data.startDate) } : {}),
    ...(data.endDate !== undefined ? { endDate: formatNoticeDateKey(data.endDate) } : {}),
  };
}

export const getNotices = async (params?: NoticeListQuery): Promise<NoticeWithCompany[]> => {
  const response = await httpClient.get<NoticeWithCompany[]>("/notices", { params });
  return response.data;
};

export const getNotice = async (id: string): Promise<NoticeWithCompany | null> => {
  const response = await httpClient.get<NoticeWithCompany | null>(`/notices/${id}`);
  return response.data;
};

export const getActiveNotice = async (params?: {
  target?: "APP" | "DASHBOARD";
  companyId?: string;
}): Promise<Notice | null> => {
  const response = await httpClient.get<Notice | null>("/notices/active", { params });
  return response.data;
};

export const createNotice = async (data: CreateNoticePayload): Promise<NoticeWithCompany> => {
  const response = await httpClient.post<NoticeWithCompany>("/notices", normalizeNoticeDates(data));
  return response.data;
};

export const updateNotice = async (
  id: string,
  data: UpdateNoticePayload,
): Promise<NoticeWithCompany> => {
  const response = await httpClient.put<NoticeWithCompany>(
    `/notices/${id}`,
    normalizeNoticeDates(data),
  );
  return response.data;
};

export const deleteNotice = async (id: string): Promise<Notice> => {
  const response = await httpClient.delete<Notice>(`/notices/${id}`);
  return response.data;
};
